import React from 'react';
import { Table, Button, Modal, Form, Input, InputNumber, DatePicker, Select, Switch, Popconfirm, notification, Typography, Space, Tag } from 'antd';
import { PlusOutlined, DollarOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import moment from 'moment';

import { useAuth } from '../../authentication/AuthProvider';
import request from '../../lib/request';
import { useCompanyId } from '../../lib/books';

import { date as fmtDate } from '../../lib/format';
const money = (v) => Number(v).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/** Current standard hourly cost of a level: the latest rate whose effective date is not in the future. */
export function currentRate(designation, onDate) {
  const today = onDate || moment().format('YYYY-MM-DD');
  const rates = (designation.DesignationHourlyRates || []).filter((r) => r.effective_date <= today);
  if (!rates.length) return null;
  return [...rates].sort((a, b) => (a.effective_date < b.effective_date ? 1 : -1))[0];
}

/**
 * Career Levels (xlsx "Team Structure (Career levels)" 1-Assistant ..
 * 10-Partner) with the hourly cost history per level. A System
 * Administrator with no company selected edits the ADMIN template that new
 * companies import.
 */
export default function DesignationsPage() {
  const auth = useAuth();
  const isSystemAdmin = auth.user.role === 'SYSTEM_ADMIN';
  const isManager = isSystemAdmin || auth.user.role === 'COMPANY_MANAGER';
  const { companyId, setCompanyId } = useCompanyId();
  const [companies, setCompanies] = React.useState([]);
  const [data, setData] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [editing, setEditing] = React.useState(null);
  const [editorOpen, setEditorOpen] = React.useState(false);
  const [rateModalFor, setRateModalFor] = React.useState(null);
  const [form] = Form.useForm();
  const [rateForm] = Form.useForm();

  React.useEffect(() => {
    if (isSystemAdmin) {
      request
        .get('/companies')
        .then((r) => r.json())
        .then((r) => r.status && setCompanies(r.data));
    }
  }, [isSystemAdmin]);

  const fetchLevels = () => {
    setLoading(true);
    request
      .get('/designations', companyId ? { company_id: companyId } : {})
      .then((r) => r.json())
      .then((r) => r.status && setData(r.data))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(fetchLevels, [companyId]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue({ level_order: (Math.max(0, ...data.map((d) => d.level_order || 0)) || 0) + 1, is_active: true });
    setEditorOpen(true);
  };
  const openEdit = (d) => {
    setEditing(d);
    form.setFieldsValue(d);
    setEditorOpen(true);
  };

  const handleSave = () => {
    form.validateFields().then((values) => {
      const payload = { ...values, company_id: companyId };
      (editing ? request.put(`/designations/${editing.id}`, payload) : request.post('/designations', payload))
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: editing ? 'Career level updated' : 'Career level created' });
            setEditorOpen(false);
            fetchLevels();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const handleAddRate = () => {
    rateForm.validateFields().then((values) => {
      request
        .post(`/designations/${rateModalFor.id}/hourly-rates`, { ...values, effective_date: values.effective_date.format('YYYY-MM-DD') })
        .then((r) => r.json())
        .then((r) => {
          if (r.status) {
            notification.success({ message: 'Hourly cost added' });
            setRateModalFor(null);
            fetchLevels();
          } else {
            notification.error({ message: r.message });
          }
        });
    });
  };

  const removeRate = (designation, rate) => {
    request
      .delete(`/designations/${designation.id}/hourly-rates/${rate.id}`)
      .then((r) => r.json())
      .then((r) => (r.status ? fetchLevels() : notification.error({ message: r.message })));
  };

  const columns = [
    { title: 'Level', dataIndex: 'level_order', width: 80, render: (v) => <Tag color="blue">{v}</Tag> },
    { title: 'Name', dataIndex: 'name', render: (v, d) => (d.is_active ? v : <Typography.Text delete type="secondary">{v}</Typography.Text>) },
    { title: 'Description', dataIndex: 'description', render: (v) => v || '-' },
    {
      title: 'Hourly cost (current)',
      key: 'rate',
      render: (_, d) => {
        const rate = currentRate(d);
        return rate ? `EUR ${money(rate.hourly_rate)} (from ${rate.effective_date})` : <Tag>Not set</Tag>;
      },
    },
    { title: 'Rate history', key: 'history', render: (_, d) => `${(d.DesignationHourlyRates || []).length} entr${(d.DesignationHourlyRates || []).length === 1 ? 'y' : 'ies'}` },
    ...(isManager
      ? [
          {
            title: '',
            key: 'actions',
            render: (_, d) => (
              <Space>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(d)}>
                  Edit
                </Button>
                <Button size="small" icon={<DollarOutlined />} onClick={() => setRateModalFor(d)}>
                  Add cost
                </Button>
              </Space>
            ),
          },
        ]
      : []),
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
        <div>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Career Levels
          </Typography.Title>
          <Typography.Text type="secondary">Team structure from Assistant to Partner, with the hourly cost per level kept as history.</Typography.Text>
        </div>
        <Space>
          {isSystemAdmin && (
            <Select
              allowClear
              style={{ width: 220 }}
              value={companyId}
              placeholder="ADMIN template"
              onChange={(v) => setCompanyId(v || null)}
              options={companies.map((c) => ({ value: c.id, label: c.name }))}
            />
          )}
          {isManager && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New Level
            </Button>
          )}
        </Space>
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={data}
        loading={loading}
        pagination={false}
        expandable={{
          rowExpandable: (d) => (d.DesignationHourlyRates || []).length > 0,
          expandedRowRender: (d) => (
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={d.DesignationHourlyRates}
              columns={[
                { title: 'Effective from', dataIndex: 'effective_date', render: (v) => fmtDate(v) },
                { title: 'Hourly cost', dataIndex: 'hourly_rate', render: (v) => `EUR ${money(v)}` },
                ...(isManager
                  ? [
                      {
                        title: '',
                        key: 'del',
                        render: (_, r) => (
                          <Popconfirm title="Remove this rate entry?" onConfirm={() => removeRate(d, r)}>
                            <Button type="text" size="small" danger icon={<DeleteOutlined />} />
                          </Popconfirm>
                        ),
                      },
                    ]
                  : []),
              ]}
            />
          ),
        }}
      />

      <Modal title={editing ? `Edit ${editing.name}` : 'New Career Level'} open={editorOpen} onCancel={() => setEditorOpen(false)} onOk={handleSave}>
        <Form form={form} layout="vertical">
          <Form.Item name="level_order" label="Level number" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={1} max={99} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input placeholder="e.g. Senior, Manager, Partner" maxLength={255} />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>

      <Modal title={`Add hourly cost - ${rateModalFor?.name || ''}`} open={!!rateModalFor} onCancel={() => setRateModalFor(null)} onOk={handleAddRate}>
        <Typography.Paragraph type="secondary">Costs are never edited in place: add a new entry from the date the new cost applies and timesheets before that date keep the old cost.</Typography.Paragraph>
        <Form form={rateForm} layout="vertical" initialValues={{ effective_date: moment() }}>
          <Form.Item name="effective_date" label="Effective from" rules={[{ required: true }]}>
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="hourly_rate" label="Hourly cost (EUR)" rules={[{ required: true }]}>
            <InputNumber style={{ width: '100%' }} min={0} precision={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
